import { Head, Link, router, usePage } from "@inertiajs/react"
import type { VariantProps } from "class-variance-authority"
import {
  Check,
  ChevronRight,
  Copy,
  Globe,
  Link2,
  Search,
  Settings,
} from "lucide-react"
import { useState } from "react"

import { LandingPageCtaClicks } from "@/components/landing-page-cta-clicks"
import {
  type LandingHero,
  LandingHeroSheet,
} from "@/components/partner/landing-hero-sheet"
import { StatCard } from "@/components/partner-dashboard/stat-card"
import { Badge, type badgeVariants } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Empty } from "@/components/ui/empty"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import PartnerLayout from "@/layouts/partner-layout"
import { cn } from "@/lib/utils"
import { partnerAccountPath } from "@/routes"

type BadgeVariant = VariantProps<typeof badgeVariants>["variant"]

type TabKey = "main-page" | "client-pages"

// Split by surface rather than by row shape — the two tables differ precisely
// because the pages do. Rendered as the client app's Conversations filter
// pills: outlined for the active one, ghost text for the rest.
const TABS: { key: TabKey; label: string }[] = [
  { key: "main-page", label: "Main page" },
  { key: "client-pages", label: "Client pages" },
]

// What came out of the assessment: a customer, a prospect, or nothing. A
// failed lookup isn't a status — the interview carries on answers-only and
// can still finish, so those land wherever they end up like any other.
// Submissions we pre-screen out never become assessments and never show here.
type Status = "activated" | "prospect" | "abandoned"

// Same shape and labels as AccountStateBadge, so a row's status reads the
// same as the account it becomes.
const STATUS_BADGE: Record<Status, { label: string; variant: BadgeVariant }> = {
  activated: { label: "Activated", variant: "operational" },
  prospect: { label: "Prospect", variant: "info" },
  abandoned: { label: "Abandoned", variant: "draft" },
}

interface FunnelRow {
  site_host: string
  business_name: string | null
  status: Status
  account_uid: string | null
  created_at: string
}

interface LandingPageSession {
  id: number
  created_at: string
  account: { uid: string; name: string }
  location: string | null
  ip_address: string | null
  cta_counts: Record<string, number>
}

// IMP-512/513: the funnel over the stats window, every step per business.
interface MarketingStats {
  window_days: number
  page_visits: number
  assessments_started: number
  prospects: number
  activated: number
  assessment_share: number | null
  prospect_share: number | null
  activated_share: number | null
}

function formatSessionTime(iso: string) {
  const d = new Date(iso)
  return {
    time: d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
    date: d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
  }
}

// The mockup's relative dates: "Today, 9:38am", "Yesterday", "3 Aug" (with
// the year once it differs).
function formatCreated(iso: string) {
  const d = new Date(iso)
  const now = new Date()
  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const dayDiff = (startOfDay(now) - startOfDay(d)) / 86_400_000

  if (dayDiff === 0) {
    const time = d
      .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
      .toLowerCase()
      .replace(" ", "")
    return `Today, ${time}`
  }
  if (dayDiff === 1) return "Yesterday"
  const dayMonth = `${d.getDate()} ${d.toLocaleDateString("en-US", { month: "short" })}`
  return d.getFullYear() === now.getFullYear()
    ? dayMonth
    : `${dayMonth} ${d.getFullYear()}`
}

export default function MarketingIndex({
  landing_page_url,
  hero,
  stats,
  funnel,
  sessions,
}: {
  landing_page_url: string | null
  hero: LandingHero
  stats: MarketingStats
  funnel: FunnelRow[]
  sessions: LandingPageSession[]
}) {
  const { partner } = usePage<{ partner: { name: string } | null }>().props
  const [copied, setCopied] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [tab, setTab] = useState<TabKey>("main-page")

  function copyHubUrl() {
    if (!landing_page_url) return
    void navigator.clipboard.writeText(landing_page_url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <PartnerLayout>
      <Head title="Marketing" />
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div>
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-2xl/8 font-semibold">Marketing</h1>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={() => setSettingsOpen(true)}
            >
              <Settings className="size-4" />
              Settings
            </Button>
          </div>

          {/* Hub link is the only meta detail under the heading */}
          {landing_page_url && (
            <div className="mt-2.5 flex items-center gap-3 text-sm/6">
              <Link2 className="text-muted-foreground size-4 shrink-0" />
              <a
                href={landing_page_url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline-offset-2 hover:underline"
              >
                {landing_page_url.replace(/^https?:\/\//, "")}
              </a>
              {/* -ml-1 cancels the button's own inset so the icon sits the same
                  distance from the URL as the link icon on the other side. */}
              <button
                type="button"
                aria-label="Copy link"
                onClick={copyHubUrl}
                className="text-muted-foreground hover:text-foreground hover:bg-muted -ml-1 grid size-6 place-items-center rounded-md transition-colors"
              >
                {copied ? (
                  <Check className="size-3.5" />
                ) : (
                  <Copy className="size-3.5" />
                )}
              </button>
            </div>
          )}
        </div>

        <LandingHeroSheet
          hero={hero}
          partnerName={partner?.name ?? "us"}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
        />

        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title="Main page visits"
            value={stats.page_visits}
            subLabel={`Last ${stats.window_days} days`}
            tooltip="Visits to your main landing page, where the demo runs."
          />
          <StatCard
            title="Demos started"
            value={stats.assessments_started}
            subValue={
              stats.assessment_share === null
                ? undefined
                : `${stats.assessment_share}%`
            }
            subLabel={
              stats.assessment_share === null
                ? `Last ${stats.window_days} days`
                : "of visits"
            }
            tooltip="Businesses that entered their website and met the assistant."
          />
          <StatCard
            title="Prospects"
            value={stats.prospects}
            subValue={
              stats.prospect_share === null
                ? undefined
                : `${stats.prospect_share}%`
            }
            subLabel={
              stats.prospect_share === null
                ? `Last ${stats.window_days} days`
                : "of assessments"
            }
            tooltip="Demos that finished and left contact details. Each one becomes a prospect account under you."
          />
          <StatCard
            title="Activated"
            value={stats.activated}
            subValue={
              stats.activated_share === null
                ? undefined
                : `${stats.activated_share}%`
            }
            subLabel={
              stats.activated_share === null
                ? `Last ${stats.window_days} days`
                : "of prospects"
            }
            tooltip="Prospects that bought a job during or after the demo."
          />
        </div>

        {/* Filter pills + table */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            {TABS.map((t) => (
              <Button
                key={t.key}
                size="sm"
                variant={tab === t.key ? "outline" : "ghost"}
                className={cn(
                  "h-8 rounded-full",
                  tab !== t.key && "text-muted-foreground",
                )}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </Button>
            ))}
          </div>

          {tab === "main-page" && <FunnelTable rows={funnel} />}
          {tab === "client-pages" && <SessionsTable sessions={sessions} />}
        </div>
      </div>
    </PartnerLayout>
  )
}

function FunnelTable({ rows }: { rows: FunnelRow[] }) {
  if (rows.length === 0) {
    return (
      <Empty
        icon={<Search className="h-5 w-5" />}
        title="No demos yet"
        description="Businesses that run the assessment on your main page will show up here."
      />
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Business</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="w-0" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          // Abandoned assessments never produced an account, so there is
          // nothing to open — no chevron, no hover, no pointer.
          const opensAccount = row.status !== "abandoned" && row.account_uid
          return (
            <TableRow
              key={row.site_host}
              // The base TableRow hovers; inert abandoned rows must not.
              className={cn(
                opensAccount
                  ? "hover:bg-muted/50 cursor-pointer"
                  : "hover:bg-transparent",
              )}
              onClick={
                opensAccount
                  ? () =>
                      router.visit(
                        `${partnerAccountPath({ id: row.account_uid! })}?tab=marketing`,
                      )
                  : undefined
              }
            >
              <TableCell className="text-sm">
                <div className="font-medium">
                  {row.business_name ?? (
                    <span className="text-muted-foreground">
                      {row.site_host}
                    </span>
                  )}
                </div>
                {row.business_name && (
                  <div className="text-muted-foreground">{row.site_host}</div>
                )}
              </TableCell>
              <TableCell className="text-sm">
                <Badge variant={STATUS_BADGE[row.status].variant}>
                  {STATUS_BADGE[row.status].label}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {formatCreated(row.created_at)}
              </TableCell>
              <TableCell className="w-0">
                {opensAccount && (
                  <ChevronRight className="text-muted-foreground size-4" />
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

// The Client pages tab is the previous "Recent visits" table, unchanged —
// same columns, same cells, same LandingPages::SessionHistory shape.
function SessionsTable({ sessions }: { sessions: LandingPageSession[] }) {
  if (sessions.length === 0) {
    return (
      <Empty
        icon={<Globe className="h-5 w-5" />}
        title="No sessions yet"
        description="Visits to your accounts' receptionist landing pages will show up here."
      />
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Session</TableHead>
          <TableHead>Visitor</TableHead>
          <TableHead>CTA clicks</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sessions.map((session) => {
          const { time, date } = formatSessionTime(session.created_at)
          return (
            <TableRow key={session.id}>
              <TableCell className="text-sm">
                <Link
                  href={partnerAccountPath({ id: session.account.uid })}
                  className="underline underline-offset-2"
                >
                  {session.account.name}
                </Link>
                <div className="text-muted-foreground">
                  {time}, {date}
                </div>
              </TableCell>
              <TableCell className="text-sm">
                <div>
                  {session.ip_address ?? (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
                <div className="text-muted-foreground">
                  {session.location ?? "—"}
                </div>
              </TableCell>
              <TableCell className="text-sm">
                <LandingPageCtaClicks ctaCounts={session.cta_counts} />
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
